// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Projects stored analysis datasets into per-document agent run history.
 *
 * Pipeline persistence is dataset-oriented, but reviewers navigate by document
 * and agent. This read-only adapter joins those identities without duplicating
 * results or teaching table components the dataset key/version encoding.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import type { NodeApiClient } from '@/app-shell/services'
import type { Agent } from '@/subsystems/agents'
import {
  fetchAnalysisStatuses,
  type AnalysisStatusResponse,
} from '@/applications/shared/services/dataset'

import { rendererRef } from '@/subsystems/agents'

export interface AgentRunVersion {
  version: number
  updatedAt: string | null
}

export interface AgentRunHistory {
  agentId: string
  label: string
  pipelineId: string
  rendererId: string
  latestAt: string | null
  status: 'completed' | 'error'
  error: string | null
  versions: AgentRunVersion[]
}

export type WorkspaceAgentRuns = Map<string, AgentRunHistory[]>

function statusKey(key: string) {
  const marker = key.lastIndexOf('_v')
  if (marker < 1) return null
  const version = Number.parseInt(key.slice(marker + 2), 10)
  if (!Number.isInteger(version) || version < 1) return null
  return { documentId: key.slice(0, marker), version }
}

function latestDate(left: string | null, right: string | null) {
  if (!left) return right
  if (!right) return left
  return Date.parse(left) >= Date.parse(right) ? left : right
}

export function buildWorkspaceAgentRuns(
  agents: Agent[],
  response: AnalysisStatusResponse,
): WorkspaceAgentRuns {
  const byDocument: WorkspaceAgentRuns = new Map()

  for (const agent of agents) {
    if (!agent.pipelineId) continue
    const renderers = Object.entries(agent.renderers).sort(
      ([, left], [, right]) => (left.order ?? 0) - (right.order ?? 0),
    )
    const primary = renderers.find(([, renderer]) => Boolean(renderer.component)) ?? renderers[0]
    if (!primary) continue

    const versionsByDocument = new Map<string, Map<number, string | null>>()
    const statusByDocument = new Map<string, {
      latestAt: string | null
      status: 'completed' | 'error'
      error: string | null
    }>()

    // Every dataset under this agent is evidence of a run. Results are scoped by
    // agent, so the response already holds exactly this agent's datasets — no
    // need to derive the set from the renderers that happen to display them.
    for (const entries of Object.values(response.agents[agent.agentId] ?? {})) {
      for (const [key, entry] of Object.entries(entries)) {
        const parsed = statusKey(key)
        if (!parsed) continue
        const status = statusByDocument.get(parsed.documentId) ?? {
          latestAt: null,
          status: 'completed' as const,
          error: null,
        }
        if (latestDate(status.latestAt, entry.d ?? null) !== status.latestAt) {
          status.latestAt = entry.d ?? null
          status.status = entry.t === 'error' ? 'error' : 'completed'
          status.error = entry.e ?? null
          statusByDocument.set(parsed.documentId, status)
        }

        const completedAt = entry.sd ?? null
        if (!completedAt) continue
        const versions = versionsByDocument.get(parsed.documentId) ?? new Map()
        versions.set(
          parsed.version,
          latestDate(versions.get(parsed.version) ?? null, completedAt),
        )
        versionsByDocument.set(parsed.documentId, versions)
      }
    }

    for (const documentId of new Set([...versionsByDocument.keys(), ...statusByDocument.keys()])) {
      const versions = versionsByDocument.get(documentId) ?? new Map()
      const status = statusByDocument.get(documentId) ?? {
        latestAt: null,
        status: 'completed' as const,
        error: null,
      }
      const runVersions = [...versions]
        .map(([version, updatedAt]) => ({ version, updatedAt }))
        .sort((left, right) => right.version - left.version)
      const history: AgentRunHistory = {
        agentId: agent.agentId,
        label: agent.label,
        pipelineId: agent.pipelineId,
        rendererId: rendererRef(agent.agentId, primary[0]),
        latestAt: latestDate(status.latestAt, runVersions.reduce<string | null>(
          (latest, run) => latestDate(latest, run.updatedAt),
          null,
        )),
        status: status.status,
        error: status.error,
        versions: runVersions,
      }
      byDocument.set(documentId, [...(byDocument.get(documentId) ?? []), history])
    }
  }

  for (const runs of byDocument.values()) {
    runs.sort((left, right) => (Date.parse(right.latestAt ?? '') || 0) - (Date.parse(left.latestAt ?? '') || 0))
  }
  return byDocument
}

export function useWorkspaceAgentRuns(
  apiClient: NodeApiClient,
  orgId: string,
  workspaceId: string,
  agents: Agent[],
) {
  const agentIds = useMemo(
    () => [...new Set(agents.map((agent) => agent.agentId))].sort(),
    [agents],
  )

  const query = useQuery({
    queryKey: ['workspace-agent-runs', orgId, workspaceId, agentIds],
    enabled: Boolean(orgId && workspaceId && agentIds.length),
    queryFn: () => fetchAnalysisStatuses(apiClient, orgId, workspaceId, agentIds),
  })

  return {
    runs: useMemo(
      () => buildWorkspaceAgentRuns(agents, query.data ?? { agents: {} }),
      [agents, query.data],
    ),
    refresh: query.refetch,
  }
}
